import { effectiveRir } from "./effortScale.js";
import type {
  Workout,
  WorkoutExerciseEntry,
  WorkoutSet,
} from "./schemas/workout.js";

// Heuristic for "what did I last do for this exercise that's worth
// repeating today". The athlete picks an exercise on a fresh entry; we
// look back across saved workouts and surface the matching entry the
// logger should prefill from.
//
// Selection rule (B3/B4 spec): among the workouts that contain this
// slug, prefer the one whose heaviest weighted set was at ≥ 10 reps —
// the "heavier-at-≥-10-reps wins" rule that matches how a
// strength-focused user typically structures hypertrophy work. Falls
// back to the most recent matching workout if no entry hits the rep
// floor.
//
// Tiebreaker: recency (newer wins). A weighted set of equal weight in
// an older workout never beats a fresher one once the rep floor is
// satisfied.
//
// Returns the WorkoutExerciseEntry (so the caller has the sets array
// directly) plus the source workout's startedAt for "Prefill from
// <date>?" UI copy. Null when no past workout contains the slug.

export type RecentForExercise = {
  entry: WorkoutExerciseEntry;
  startedAt: string;
};

/**
 * The heaviest weighted set of one entry, with its reps.
 *
 * One definition for a question two features ask: the history prefill wants
 * "how much was loaded here" (above a rep floor, so a heavy triple does not
 * seed a set of ten), and EQ-1's machine tray wants the same number as the
 * signature that tells two machines apart. They read the same sets off the
 * same type, and a second hand-rolled loop is how the two would eventually
 * disagree.
 *
 * A fail counts like any other set (AP-0p model): reps are the clean ones
 * done, and `fail` is a pure label, so it is not an exclusion.
 */
// Newest-first by session start. Both readers below order the same way; one
// comparator keeps the recency rule from drifting between them — the same
// lesson `heaviestWeightedSet` states about a second hand-rolled loop.
const orderByRecency = (workouts: readonly Workout[]): Workout[] =>
  [...workouts].sort((a, b) => b.startedAt.localeCompare(a.startedAt));

export const heaviestWeightedSet = (
  entry: WorkoutExerciseEntry,
  opts: { minReps?: number } = {},
): { weightKg: number; reps: number } | undefined => {
  const minReps = opts.minReps ?? 0;
  let best: { weightKg: number; reps: number } | undefined;
  for (const s of entry.sets) {
    if (s.type !== "weighted") continue;
    if (s.reps < minReps) continue;
    if (best === undefined || s.weight > best.weightKg) {
      best = { weightKg: s.weight, reps: s.reps };
    }
  }
  return best;
};

export const pickRecentForExercise = (
  workouts: readonly Workout[],
  exerciseId: string,
): RecentForExercise | null => {
  const REP_FLOOR = 10;
  // Walk newest-first so the recency tiebreaker collapses naturally:
  // the first entry that beats the running best wins. Strict-greater
  // means equal-weight older entries don't displace newer ones.
  const ordered = orderByRecency(workouts);
  let bestAtFloor: RecentForExercise | null = null;
  let bestAtFloorWeight = 0;
  let mostRecent: RecentForExercise | null = null;
  for (const w of ordered) {
    for (const entry of w.entries) {
      if (entry.exerciseId !== exerciseId) continue;
      if (!mostRecent) mostRecent = { entry, startedAt: w.startedAt };
      const heaviest =
        heaviestWeightedSet(entry, { minReps: REP_FLOOR })?.weightKg ?? 0;
      if (heaviest > bestAtFloorWeight) {
        bestAtFloor = { entry, startedAt: w.startedAt };
        bestAtFloorWeight = heaviest;
      }
      // First match in this workout is enough — multiple entries with
      // the same slug in one workout (drop-in extra round, A-B-A
      // alternation) usually share a load profile; the first appearance
      // is fine for prefill.
      break;
    }
  }
  return bestAtFloor ?? mostRecent;
};

/**
 * One recorded set of an exercise, dated and reduced to what the history
 * surfaces care about: its value (weight × reps, where the kind has them) and
 * the intensity it was done at. The shared shape behind two consumers that both
 * need "what did this exercise's recent sets look like":
 *   - the logger's exercise-history readout (LG-49) — the last few sets with
 *     their date and effort — and
 *   - the effort-axis weight fallback (PG-9): a prescription on RIR still has to
 *     land on a real weight, and the most recent loaded set is one source of it.
 *
 * ⛔ NOT the "similar exercise" fallback of the history layer
 * (entryHistoryPick.ts): that ranks catalog alternatives when an exercise has no
 * history of its own, a separate, UI-owned decision. This answers only the
 * literal question — the sets THIS exercise has — and returns [] when none.
 */
export type RecentSet = {
  /** When the set was done, ms since epoch. The set's own `completedAt` when
   *  present, else the session's `startedAt`: completedAt is optional and often
   *  absent, so the session start is the honest fallback, never a guess. */
  atMs: number;
  /** The set's kind, so a consumer renders a weighted set's kg differently from
   *  a bodyweight rep count or a timed hold. */
  type: WorkoutSet["type"];
  /** Per-implement load in kg, for the kinds that carry one (weighted,
   *  loaded-carry, weighted timed hold, cardio sled). `null` for the kinds with
   *  no absolute kg load: bodyweight (its `extra` is a delta on bodyweight, a
   *  different quantity) and p1rm. */
  weightKg: number | null;
  /** Reps, for the kinds that count them; `null` for the time-only kinds
   *  (cardio, timed). */
  reps: number | null;
  /** The recorded intensity: effective RIR (fail→0, else RIR, else RPE→RIR).
   *  `null` when the set carried no effort at all — unknown, not zero, and not
   *  comparable to a set that has one (effortScale.ts). */
  rir: number | null;
};

const DEFAULT_RECENT_LIMIT = 5;

/**
 * The last `limit` sets an athlete logged for one exercise, newest-first.
 *
 * Ordering: sessions by recency (`startedAt` desc); within one session the sets
 * are returned last-logged-first (reverse of their stored, performed order),
 * across every matching entry of that session. `completedAt` is NOT used to
 * order sets inside a session — it is optional and usually absent, so a partial
 * set of timestamps would sort some sets and not others; the stored order is the
 * reliable proxy for "which came later".
 *
 * ⛔ Unlike `pickRecentForExercise`, this does NOT stop at the first matching
 * entry of a session: that function prefills from ONE load profile, so a second
 * same-slug entry is redundant to it; this one enumerates the actual sets, so
 * dropping any would answer a different question than "the last N sets".
 *
 * Pure and offline: it reads the already-cached window the caller passes and
 * makes no network call — both consumers require it to work with no connection.
 * It stops as soon as `limit` sets are collected, so it never walks the whole
 * history to return a handful of rows.
 */
export const recentSetsForExercise = (
  workouts: readonly Workout[],
  exerciseId: string,
  limit: number = DEFAULT_RECENT_LIMIT,
): RecentSet[] => {
  if (limit <= 0) return [];
  const ordered = orderByRecency(workouts);
  const out: RecentSet[] = [];
  for (const w of ordered) {
    for (const entry of w.entries) {
      if (entry.exerciseId !== exerciseId) continue;
      // Last-logged first within the session (reverse of performed order).
      for (let i = entry.sets.length - 1; i >= 0; i--) {
        const s = entry.sets[i]!;
        out.push({
          atMs: Date.parse(s.completedAt ?? w.startedAt),
          type: s.type,
          weightKg:
            "weight" in s && s.weight !== undefined ? s.weight : null,
          reps: "reps" in s && s.reps !== undefined ? s.reps : null,
          rir: effectiveRir(s),
        });
        if (out.length >= limit) return out;
      }
    }
  }
  return out;
};
